import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trophy, Save } from "lucide-react";

// GRANDE FINAL admin panel (user request Feb-2026). Lets the admin:
//   • toggle visibility of the free-training mode on the public home,
//   • rename the mode in each of the 3 supported languages.
// Backing config lives in `/api/grande-final/config` (singleton doc).
export default function AdminGrandeFinal() {
  const [cfg, setCfg] = useState({
    enabled: false,
    name_pt: "GRANDE FINAL",
    name_en: "GRAND FINAL",
    name_es: "GRAN FINAL",
    subtitle_pt: "",
    subtitle_en: "",
    subtitle_es: "",
    glow_enabled: true,
    pulse_enabled: true,
    offset_y: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/grande-final/config");
      setCfg({
        enabled: !!data.enabled,
        name_pt: data.name_pt || "GRANDE FINAL",
        name_en: data.name_en || "GRAND FINAL",
        name_es: data.name_es || "GRAN FINAL",
        subtitle_pt: data.subtitle_pt || "",
        subtitle_en: data.subtitle_en || "",
        subtitle_es: data.subtitle_es || "",
        glow_enabled: data.glow_enabled !== false,
        pulse_enabled: data.pulse_enabled !== false,
        offset_y: Number.isFinite(data.offset_y) ? data.offset_y : 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falhou a carregar configuração");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/grande-final/config", cfg);
      toast.success("Configuração guardada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <header className="flex items-center gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-3 shadow-md">
          <Trophy className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-[Fredoka] font-extrabold text-slate-900">Grande Final (Treino)</h1>
          <p className="text-sm text-slate-500">
            Novo separador de treino que dá acesso livre a todos os jogos de competição,
            sem credenciais e sem registo de resultados.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-slate-500">A carregar…</div>
      ) : (
        <>
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">
                  Mostrar o separador no público
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Quando ligado, aparece um botão horizontal na secção TREINOS logo abaixo
                  de VS TEMPO / VS ROBOT / VS HUMANO.
                </p>
              </div>
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: !!v }))}
                data-testid="gf-toggle-enabled"
              />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">
              Efeitos em redor do botão
            </h3>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Brilho (glow)</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Halo dourado externo que destaca o botão sobre o fundo escuro.
                </p>
              </div>
              <Switch
                checked={cfg.glow_enabled}
                onCheckedChange={(v) => setCfg((c) => ({ ...c, glow_enabled: !!v }))}
                data-testid="gf-toggle-glow"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Pulsação animada</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  O brilho pulsa suavemente a cada 2,4 s para chamar a atenção.
                </p>
              </div>
              <Switch
                checked={cfg.pulse_enabled}
                onCheckedChange={(v) => setCfg((c) => ({ ...c, pulse_enabled: !!v }))}
                data-testid="gf-toggle-pulse"
              />
            </div>

            <div className="pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-slate-800">Posição vertical ({cfg.offset_y ?? 0}px)</p>
                <button
                  type="button"
                  onClick={() => setCfg((c) => ({ ...c, offset_y: 0 }))}
                  className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                  data-testid="gf-offset-reset"
                >
                  repor
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                Afasta ou aproxima a caixa GRANDE FINAL das 3 caixas (VS TEMPO / VS ROBOT / VS HUMANO)
                dentro da caixa TREINOS. Valores negativos sobem; positivos descem.
              </p>
              <input
                type="range"
                min={-200}
                max={200}
                step={1}
                value={cfg.offset_y ?? 0}
                onChange={(e) => setCfg((c) => ({ ...c, offset_y: Number(e.target.value) }))}
                className="w-full"
                data-testid="gf-offset-y"
              />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">
              Nome do separador (por idioma)
            </h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>🇵🇹 Português</Label>
                <Input
                  value={cfg.name_pt}
                  onChange={(e) => setCfg({ ...cfg, name_pt: e.target.value })}
                  maxLength={80}
                  data-testid="gf-name-pt"
                />
              </div>
              <div>
                <Label>🇬🇧 English</Label>
                <Input
                  value={cfg.name_en}
                  onChange={(e) => setCfg({ ...cfg, name_en: e.target.value })}
                  maxLength={80}
                  data-testid="gf-name-en"
                />
              </div>
              <div>
                <Label>🇪🇸 Español</Label>
                <Input
                  value={cfg.name_es}
                  onChange={(e) => setCfg({ ...cfg, name_es: e.target.value })}
                  maxLength={80}
                  data-testid="gf-name-es"
                />
              </div>
            </div>

            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600 pt-2">
              Subtítulo (opcional)
            </h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>🇵🇹 PT</Label>
                <Input
                  value={cfg.subtitle_pt}
                  onChange={(e) => setCfg({ ...cfg, subtitle_pt: e.target.value })}
                  maxLength={160}
                  data-testid="gf-sub-pt"
                />
              </div>
              <div>
                <Label>🇬🇧 EN</Label>
                <Input
                  value={cfg.subtitle_en}
                  onChange={(e) => setCfg({ ...cfg, subtitle_en: e.target.value })}
                  maxLength={160}
                  data-testid="gf-sub-en"
                />
              </div>
              <div>
                <Label>🇪🇸 ES</Label>
                <Input
                  value={cfg.subtitle_es}
                  onChange={(e) => setCfg({ ...cfg, subtitle_es: e.target.value })}
                  maxLength={160}
                  data-testid="gf-sub-es"
                />
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
              data-testid="gf-save-btn"
            >
              <Save className="w-4 h-4 mr-2" /> Guardar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
